import { ReactNode, useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useListFamilyAnnotations } from "../../services/engagement";
import Loading from "../common/Loading";
import { MessageSquare } from "lucide-react";
import { useListInvoices } from "../../services/billing";
import {
  Invoice,
  ListInvoicesRequest,
} from "../../protogen/billing_service_pb";
import LinkRouter from "../navigation/LinkRouter";
import AnnotationCard from "components/engagement/notes/AnnotationCard";
import InvoiceCard from "components/engagement/notes/InvoiceCard";
import AddAnnotation, {
  AddAnnotationRef,
} from "components/engagement/notes/AddAnnotation";
import ChangeEventCard from "components/engagement/notes/ChangeEventCard";
import InvoiceChangeCard from "./InvoiceChangeCard";
import { CHECKIN_FLAGS } from "components/engagement/flags";

const TYPE_OPTIONS = ["billing"];

interface Props {
  invoice: Invoice;
}

export default ({ invoice }: Props) => {
  const familyRef = invoice.family?.ref;
  const advisor = invoice.advisor;
  const editorRef = useRef<AddAnnotationRef>(null);
  const {
    request: listInvoices,
    loading: invoicesLoading,
    data: invoicesResponse,
  } = useListInvoices();
  const {
    request: listAnnotations,
    data: annotationsResponse,
    loading: annotationsLoading,
  } = useListFamilyAnnotations();

  useEffect(() => {
    // familyRef is always set by the caller.
    listAnnotations({ familyRef: familyRef ?? "" });
    listInvoices(
      new ListInvoicesRequest({
        familyRef: familyRef ?? "",
      }),
    );
  }, [familyRef]);
  const loading =
    (invoicesLoading || annotationsLoading) &&
    !(annotationsResponse && invoicesResponse);
  // Merge results
  const feedEntries: [number, ReactNode][] = [
    ...(annotationsResponse?.annotations || []).map(
      (annotation): [number, ReactNode] => [
        Number(annotation.lastUpdatedSec),
        <AnnotationCard
          key={`annotation-${annotation.ref}`}
          annotation={annotation}
        />,
      ],
    ),
    // Subpayments are internal payout splits, not money the family paid.
    ...(invoicesResponse?.invoices || [])
      .filter((invoice) => invoice.transactionType !== "split-payout")
      .map((invoice): [number, ReactNode] => [
        Number(invoice.createdSec),
        <InvoiceCard key={`invoice-${invoice.ref}`} invoice={invoice} />,
      ]),
    ...(annotationsResponse?.changeEvents || []).map(
      (annotation): [number, ReactNode] => [
        Number(annotation.lastUpdatedSec),
        <ChangeEventCard
          key={`annotation-${annotation.ref}`}
          annotation={annotation}
        />,
      ],
    ),
  ];
  invoice.changeEvents.map((changeEvent, i) => {
    feedEntries.push([
      Number(invoice.createdSec),
      <InvoiceChangeCard
        key={`invoice-change-${invoice.ref}-${i}`}
        changeEvent={changeEvent}
      />,
    ]);
  });

  feedEntries.sort((a, b) => b[0] - a[0]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "16px",
        alignSelf: "stretch",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"16px"}
        width={"100%"}
      >
        <AddAnnotation
          forceOpen={false}
          ref={editorRef}
          familyRef={familyRef}
          signupSessionRef={""}
          placeholder="Add a note"
          onUpdate={async () => {
            await listAnnotations({ familyRef: familyRef });
          }}
          noteTypes={TYPE_OPTIONS}
          includeFlags={true}
          flagOptions={CHECKIN_FLAGS}
        />
      </Box>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"16px"}
        width={"100%"}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={"16px"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Typography variant="h3">Family History</Typography>
          {advisor && (
            <Typography variant="bodySmall" color="textSecondary">
              <LinkRouter
                sx={{ alignItems: "center" }}
                targetNew={true}
                to={`/inbox/messages?recipientRefs=${encodeURIComponent(
                  advisor.ref,
                )}`}
              >
                <MessageSquare size={12} style={{ marginRight: 4 }} />
                Message {advisor.firstName}
              </LinkRouter>
            </Typography>
          )}
        </Box>
        {loading && <Loading />}
        {feedEntries.map((entry) => entry[1])}
      </Box>
    </Box>
  );
};
